// The task captured from this card ("Task: Reply to Sam about the pilot"), with where it stands
// now and a link to it on the Tasks screen.
import { Link } from "@tanstack/react-router";
import { CircleCheck, ListTodo } from "lucide-react";
import type { CardBlock } from "@shared/core";
import { cn } from "@web/lib/cn";
import { useTask } from "@web/lib/tasksQueries";
import { useNow } from "@web/lib/useNow";
import { tasksSearchFor } from "@web/tasks/tasksSearch";
import { doneLabel, dueLabel, snoozedLabel, taskViewFor } from "@web/tasks/wording";

export function TaskRefBlock({ block }: { block: CardBlock }) {
  const now = useNow(60_000);
  const task = useTask(block.taskId);
  const title = task.data?.title ?? block.value ?? block.text ?? "Task";
  const t = task.data;
  const state = !t
    ? null
    : t.status === "done"
      ? doneLabel(t, now)
      : t.status === "snoozed"
        ? snoozedLabel(t, now)
        : dueLabel(t, now);
  const view = t ? taskViewFor(t, now) : null;
  const done = t?.status === "done";
  const dropped = t?.status === "dropped";
  return (
    <section
      aria-label="Task"
      className="flex items-start gap-2.5 rounded-lg border border-line bg-bg px-3 py-2.5"
    >
      {done ? (
        <CircleCheck aria-hidden className="mt-0.5 size-4 shrink-0 text-calm" />
      ) : (
        <ListTodo aria-hidden className="mt-0.5 size-4 shrink-0 text-muted" />
      )}
      <div className="min-w-0 flex-1 text-sm">
        <p className={cn("text-ink", dropped && "text-muted line-through")}>
          <span className="font-medium">Task:</span> {title}
        </p>
        {state && !dropped ? <p className="text-muted">{state}</p> : null}
        {dropped ? <p className="text-muted">Dropped. Press T to capture it again.</p> : null}
      </div>
      {!dropped ? (
        <Link
          to="/tasks"
          search={tasksSearchFor(view, block.taskId)}
          className="shrink-0 text-sm text-accent-text underline underline-offset-2"
        >
          Open in Tasks
        </Link>
      ) : null}
    </section>
  );
}
